/**
 * Environment Configuration
 *
 * Central location for all environment variables and configuration.
 * In development, these default to local values.
 * In production, they'll be loaded from actual environment variables.
 *
 * TODO: [CONTRACTOR] Load from actual environment variables
 * TODO: [CONTRACTOR] Add validation to ensure required vars are present
 * TODO: [CONTRACTOR] Add .env.example file with all required variables
 */

import { Brand } from '@enfomentis/shared/config';

export const Environment = {
  // API Configuration
  API_URL: import.meta.env.VITE_API_URL || 'http://localhost:3001',
  API_VERSION: import.meta.env.VITE_API_VERSION || 'v1',
  
  // Stripe Configuration
  STRIPE_PUBLIC_KEY: import.meta.env.VITE_STRIPE_PUBLIC_KEY || 'pk_test_dummy',
  
  // Application Info
  APP_VERSION: import.meta.env.VITE_APP_VERSION || '0.1.0',
  APP_NAME: Brand.name,
  
  // Environment Flags
  IS_PRODUCTION: import.meta.env.PROD,
  IS_DEVELOPMENT: import.meta.env.DEV,
  
  // Debug Flags
  DEBUG_MODE: import.meta.env.VITE_DEBUG_MODE === 'true',
  LOG_LEVEL: import.meta.env.VITE_LOG_LEVEL || 'info',
  
  // Feature Flags (can be overridden by env vars)
  ENABLE_ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS !== 'false',
  ENABLE_ERROR_REPORTING: import.meta.env.VITE_ENABLE_ERROR_REPORTING !== 'false',
  
  // OAuth Cloud Sync Providers
  // NOTE: With BFF (Backend-for-Frontend) pattern, only client IDs are needed in frontend.
  // Client secrets are stored server-side in Supabase Edge Function secrets.
  GOOGLE_DRIVE_CLIENT_ID: import.meta.env['VITE_GOOGLE_DRIVE_CLIENT_ID'] || '',
  ONEDRIVE_CLIENT_ID: import.meta.env['VITE_ONEDRIVE_CLIENT_ID'] || '',
  DROPBOX_APP_KEY: import.meta.env['VITE_DROPBOX_APP_KEY'] || '',
  BOX_CLIENT_ID: import.meta.env['VITE_BOX_CLIENT_ID'] || '',

  // TODO: [CONTRACTOR] Add these production configurations:
  // - SENTRY_DSN: For error tracking
  // - POSTHOG_API_KEY: For analytics
  // - CLOUDFLARE_WORKER_URL: For license validation
  
  // License Validation
  LICENSE_CHECK_INTERVAL: 1000 * 60 * 60 * 24, // 24 hours
  LICENSE_CACHE_KEY: `${Brand.cachePrefix}_license_cache`,
  
  // Storage Limits (in MB)
  MAX_STORAGE_SIZE: 500, // IndexedDB limit
  EXPORT_SIZE_WARNING: 50, // Warn if export > 50MB
  
  // Session Configuration
  SESSION_TIMEOUT: 1000 * 60 * 60 * 12, // 12 hours
  AUTO_SAVE_INTERVAL: 1000 * 30, // 30 seconds
  
  // API Rate Limits
  API_RETRY_ATTEMPTS: 3,
  API_RETRY_DELAY: 1000, // 1 second
  
  // TODO: [CONTRACTOR] Security configurations:
  // - CSP_NONCE: Content Security Policy nonce
  // - ALLOWED_ORIGINS: CORS allowed origins
  // - ENCRYPTION_KEY: For sensitive data encryption
} as const;

// Type-safe environment variable access
export type EnvironmentConfig = typeof Environment;

// Validation function for required environment variables
export function validateEnvironment(): void {
  const required = ['API_URL', 'STRIPE_PUBLIC_KEY'];
  const missing = required.filter(key => !Environment[key as keyof EnvironmentConfig]);
  
  if (missing.length > 0 && Environment.IS_PRODUCTION) {
    throw new Error(`Missing required environment variables: ${missing.join(', ')}`);
  }
}

// Development-only logging
export function logEnvironment(): void {
  if (Environment.IS_DEVELOPMENT && Environment.DEBUG_MODE) {
    console.log(`=== ${Brand.shortName} Environment Configuration ===`);
    console.log('API URL:', Environment.API_URL);
    console.log('Version:', Environment.APP_VERSION);
    console.log('Debug Mode:', Environment.DEBUG_MODE);
    console.log('===================================');
  }
}